// Initialize i18n before any component using Trans renders
import type { ErrorComponentProps } from '@tanstack/react-router';

import { I18nProvider } from '@lingui/react';
import { Trans } from '@lingui/react/macro';
import { QueryClient } from '@tanstack/react-query';
import { createRouter, useRouteContext } from '@tanstack/react-router';
import { setupRouterSsrQueryIntegration } from '@tanstack/react-router-ssr-query';
import { useEffect, useRef } from 'react';

import { i18n } from '@/i18n';

import type { PublicEnv } from './utils/session/get-public-env';

import { Button, ExternalLinkButton } from './components/base/buttons/button';
import { BASE_PATH } from './constants/config';
import { isNoisyClientException } from './lib/analytics/adapters/noisy-client-exceptions';
import { POSTHOG_CAPTURE_ENVIRONMENTS } from './lib/analytics/adapters/posthog-capture-environments';
import { useAnalytics } from './lib/analytics/use-analytics';
import {
  installStaleChunkRecovery,
  recoverFromStaleChunkLoad,
} from './lib/stale-chunk-recovery';
import { routeTree } from './routeTree.gen';
import { parseSearch, stringifySearch } from './utils/router-search-params';

// The router's default not-found / error components render *in place of* the
// root component when the failure happens at the root route, which puts them
// outside the `I18nProvider` mounted in `RootDocument`. `Trans` throws without
// a provider, so these fallbacks bring their own — a fallback must never be
// able to throw.
function NotFound() {
  return (
    <I18nProvider i18n={i18n}>
      <div className="flex min-h-screen items-center justify-center bg-primary">
        <div className="text-center">
          <h1 className="text-6xl font-bold text-primary">404</h1>
          <p className="mt-4 text-xl text-secondary">
            <Trans>Page not found</Trans>
          </p>
          <a
            href={BASE_PATH}
            className="mt-6 inline-block text-brand-primary hover:underline"
          >
            <Trans>Go home</Trans>
          </a>
        </div>
      </div>
    </I18nProvider>
  );
}

function ErrorComponent({ error }: ErrorComponentProps) {
  const analytics = useAnalytics();
  // Optional on purpose: when the failure is the root route's own
  // `beforeLoad`, there is no root context to read and destructuring it would
  // throw *inside the error boundary*. Losing PostHog capture is an acceptable
  // degradation; an error component that crashes is not.
  const rootContext = useRouteContext({ from: '__root__' });
  // Widened to `| undefined` deliberately: the type says `env` is always there
  // because the root `beforeLoad` returns it, but this component renders
  // precisely when that `beforeLoad` may not have completed.
  const env: PublicEnv | undefined = rootContext.env;
  const shouldCaptureFallback =
    !!env &&
    POSTHOG_CAPTURE_ENVIRONMENTS.has(env.ENVIRONMENT) &&
    !!env.POSTHOG_API_KEY;

  // Guard against double-reporting: the fallback can fire before AnalyticsProvider
  // initializes, then analytics.captureException fires when it becomes non-null.
  const capturedErrorRef = useRef<unknown>(null);

  useEffect(() => {
    if (capturedErrorRef.current === error) return;

    // Stale hashed assets after a deploy: one automatic reload picks up the
    // new manifest. If that already ran and the page is still broken, fall
    // through to the existing Oops / Try again UI — never loop.
    if (recoverFromStaleChunkLoad(error)) {
      capturedErrorRef.current = error;
      return;
    }

    // Noise filtering happens in PostHogAdapter.trackException, not here,
    // so GA/Segment/first-party still get errors PostHog drops.
    if (analytics) {
      analytics.captureException(error);
      capturedErrorRef.current = error;
      return;
    }

    if (!shouldCaptureFallback) return;

    // Fallback for the narrow window where a root-level error fires before
    // `AnalyticsProvider`'s useEffect has set the client. Lazy-import
    // posthog-js so it doesn't sit in the main bundle for every page load
    // just to handle this rare case.
    void (async () => {
      try {
        const { posthog } = await import('posthog-js');
        // Re-check the guard AFTER the await: `analytics` may have become
        // non-null while the import was in flight, and the effect re-ran
        // and captured the error via the analytics path. Without this
        // second check we'd report the same error twice.
        if (capturedErrorRef.current === error) return;
        // Bypasses the adapter, so repeat its filter; don't mark
        // capturedErrorRef so a later analytics mount can still fan out.
        if (isNoisyClientException(error)) return;
        if (typeof posthog.captureException === 'function') {
          posthog.captureException(error);
          capturedErrorRef.current = error;
        }
      } catch (captureError) {
        console.error('PostHog fallback captureException error', captureError);
      }
    })();
  }, [error, analytics, shouldCaptureFallback]);

  return (
    <I18nProvider i18n={i18n}>
      <div className="flex min-h-screen items-center justify-center bg-primary">
        <div className="text-center">
          <h1 className="text-6xl font-bold text-primary">
            <Trans>Oops!</Trans>
          </h1>
          <p className="mt-4 text-xl text-secondary">
            <Trans>Something went wrong</Trans>
          </p>
          <div className="mt-6 flex flex-col items-center justify-center gap-6">
            <Button onClick={() => globalThis.location.reload()}>
              <Trans>Try again</Trans>
            </Button>
            <ExternalLinkButton
              color="link-color"
              href="mailto:support@respondent.io"
            >
              <Trans>Contact support</Trans>
            </ExternalLinkButton>
          </div>
        </div>
      </div>
    </I18nProvider>
  );
}

// Create a new router instance
export const getRouter = () => {
  installStaleChunkRecovery();

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 60 * 1000, // 1 minute
        refetchOnWindowFocus: false,
      },
    },
  });

  const router = createRouter({
    routeTree,
    basepath: BASE_PATH,
    context: {
      queryClient,
      user: null,
      profile: null,
      isAdminImpersonating: false,
      adminUser: null,
      activeDashboard: { organization: null, team: null },
      env: {
        WEBSITE_URL: '',
        FACEBOOK_APPID: '',
        GOOGLE_BROWSER_APIKEY: '',
        PUSHER_APP_KEY: '',
        POSTHOG_API_KEY: '',
        INTERCOM_APP_ID: '',
        GOOGLE_ANALYTICS_MEASUREMENT_ID: '',
        GOOGLE_TAG_MANAGER_CONTAINER_ID: '',
        SEGMENT_WRITE_KEY: '',
        FIRST_PARTY_ANALYTICS_WRITE_KEY: '',
        FIRST_PARTY_ANALYTICS_HOST: '',
        ENVIRONMENT: 'local',
        RECAPTCHA_V3_SITE_KEY: '',
        RECAPTCHA_V2_SITE_KEY: '',
      },
    },
    scrollRestoration: true,
    defaultPreloadStaleTime: 0,
    defaultNotFoundComponent: NotFound,
    defaultErrorComponent: ErrorComponent,
    parseSearch,
    stringifySearch,
  });

  setupRouterSsrQueryIntegration({ router, queryClient });

  return router;
};
